import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import Cookies from 'universal-cookie';

import postLogin from 'Api/auth/postLogin';
import { useToast } from 'Store/toast';
import postLoginPayload from 'Domain/login/model/request/postLoginPayload';
import useIsMobile from 'Hook/useIsMobile';
import { useLoginState } from 'View/login/state';
import { useUserInfo } from 'Store/userInfo';
import { useLoader } from 'Store/loader';
import { errorDefaultText } from 'Const/login';

const usePostLogin = () => {
  const cookies = new Cookies();
  const navigate = useNavigate();
  const today = new Date();
  const { t } = useTranslation('login');

  const { updateState } = useLoginState();
  const { onChangeUserInfo } = useUserInfo();
  const isMobile = useIsMobile();

  const { onShowLoader, onResetLoader } = useLoader();
  const { onOpenToast, onResetToast } = useToast();

  return useMutation({
    mutationFn: async (formData) => {
      onShowLoader({ variant: 'loader-primary' });

      updateState({ errorMessage: '' });

      const payload = postLoginPayload(formData);

      return await postLogin({ payload });
    },
    onSuccess: (response) => {
      onOpenToast({
        status: 'success',
        type: isMobile ? 'mobile' : 'desktop',
        title: t('loginPage.successLoginTitle'),
        description: t('loginPage.successLoginDescription'),
        duration: 5,
        alwaysShow: false,
      });

      const { token, roleId, roleName, name, id, organization, menus } = response?.data;

      const expirationDate = new Date(today.setMonth(today.getMonth() + 1));

      const appVersion = process.env.REACT_APP_VERSION;

      onChangeUserInfo({ token, roleId, roleName, name, organization, menus });

      cookies.set('username', name, { expires: expirationDate, path: '/' });
      cookies.set('token', token, { expires: expirationDate, path: '/' });
      cookies.set('roleId', roleId, { expires: expirationDate, path: '/' });
      cookies.set('roleName', roleName, { expires: expirationDate, path: '/' });
      cookies.set('userId', id, { expires: expirationDate, path: '/' });
      cookies.set('appVersion', appVersion, { expires: expirationDate, path: '/' });
      cookies.set('organization', organization, { expires: expirationDate, path: '/' });

      const url = isMobile ? '/host' : '/';

      navigate(url);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.errors[0]?.message ?? errorDefaultText;

      onResetToast();

      updateState({ errorMessage });
    },
    onSettled: () => {
      onResetLoader();
    },
  });
};

export default usePostLogin;
